import {
  DIRECTOR_ROLES,
  DIRECTOR_ROLE_LABEL,
  type DirectorDraft,
  type DirectorRole,
  type Errors,
  type KybDraft,
  emptyDirector,
} from '~/lib/kyb';
import {
  Field,
  SectionCard,
  Select,
  TextInput,
  TwoCol,
} from './Primitives';

/**
 * Collects at least one director. The BVN is validated client-side as 11
 * digits but the backend intentionally does not persist it beyond the vendor
 * call — only `bvn_status` and the last four digits land in `kyb_subjects`.
 */
export function DirectorsStep({
  draft,
  errors,
  update,
}: {
  draft: KybDraft;
  errors: Errors;
  update: (patch: Partial<KybDraft>) => void;
}): JSX.Element {
  function patchDirector(i: number, patch: Partial<DirectorDraft>): void {
    const next = draft.directors.map((d, idx) =>
      idx === i ? { ...d, ...patch } : d,
    );
    update({ directors: next });
  }

  function addDirector(): void {
    update({ directors: [...draft.directors, emptyDirector()] });
  }

  function removeDirector(i: number): void {
    const next = draft.directors.filter((_, idx) => idx !== i);
    update({ directors: next.length > 0 ? next : [emptyDirector()] });
  }

  return (
    <SectionCard
      title="Leadership"
      description="At least one director, shareholder, or secretary. Add anyone the regulator would expect to see on the file."
    >
      <div className="flex flex-col gap-5">
        {draft.directors.map((d, i) => (
          <DirectorBlock
            key={i}
            index={i}
            value={d}
            errors={errors}
            showRemove={draft.directors.length > 1}
            onChange={(patch) => patchDirector(i, patch)}
            onRemove={() => removeDirector(i)}
          />
        ))}
        <button
          type="button"
          onClick={addDirector}
          className="inline-flex h-9 items-center gap-1.5 self-start rounded-md border border-rule-divider bg-surface-slip px-3 text-[14px] font-medium text-ink-primary hover:bg-surface-margin"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
          >
            <path d="M8 3v10M3 8h10" />
          </svg>
          Add another
        </button>
      </div>
    </SectionCard>
  );
}

function DirectorBlock({
  index,
  value,
  errors,
  showRemove,
  onChange,
  onRemove,
}: {
  index: number;
  value: DirectorDraft;
  errors: Errors;
  showRemove: boolean;
  onChange: (patch: Partial<DirectorDraft>) => void;
  onRemove: () => void;
}): JSX.Element {
  const prefix = `directors.${index}.`;
  return (
    <div className="rounded-md border border-rule-hairline bg-surface-canvas p-4">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-[13.5px] font-semibold text-ink-primary">
          Person {index + 1}
        </div>
        {showRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[13px] text-ink-watermark hover:text-stop"
          >
            Remove
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <TwoCol>
          <Field label="First name" error={errors[`${prefix}firstName`]}>
            <TextInput
              value={value.firstName}
              onChange={(v) => onChange({ firstName: v })}
              autoComplete="given-name"
              invalid={!!errors[`${prefix}firstName`]}
            />
          </Field>
          <Field label="Last name" error={errors[`${prefix}lastName`]}>
            <TextInput
              value={value.lastName}
              onChange={(v) => onChange({ lastName: v })}
              autoComplete="family-name"
              invalid={!!errors[`${prefix}lastName`]}
            />
          </Field>
        </TwoCol>

        <TwoCol>
          <Field label="Role">
            <Select<DirectorRole>
              value={value.role}
              onChange={(v) => onChange({ role: v })}
              options={DIRECTOR_ROLES.map((r) => ({
                value: r,
                label: DIRECTOR_ROLE_LABEL[r],
              }))}
            />
          </Field>
          <Field label="Nationality" error={errors[`${prefix}nationality`]}>
            <TextInput
              value={value.nationality}
              onChange={(v) => onChange({ nationality: v })}
              invalid={!!errors[`${prefix}nationality`]}
            />
          </Field>
        </TwoCol>

        <TwoCol>
          <Field
            label="BVN"
            hint="11-digit Bank Verification Number. Only used for the vendor check; we store the last four digits."
            error={errors[`${prefix}bvn`]}
          >
            <TextInput
              value={value.bvn}
              onChange={(v) => onChange({ bvn: v.replace(/[^0-9]/g, '').slice(0, 11) })}
              inputMode="numeric"
              maxLength={11}
              placeholder="22221234567"
              invalid={!!errors[`${prefix}bvn`]}
            />
          </Field>
          <Field label="Email" error={errors[`${prefix}email`]}>
            <TextInput
              value={value.email}
              onChange={(v) => onChange({ email: v })}
              type="email"
              inputMode="email"
              autoComplete="email"
              invalid={!!errors[`${prefix}email`]}
            />
          </Field>
        </TwoCol>
      </div>
    </div>
  );
}
